import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderWithoutLang from '../HeaderWithoutLang';
import Footer from '../Footer';
import { fetchLeadsByMobile } from '../../services/leadService';
import useChatStore from '../../store/useChatStore';
import showToast from '../../utility/showToast';
import { ASSETS_URL, CREDIT_API_URL } from '../../constants';
import axios from 'axios';
import DashboardProfileMenu from './DashboardProfileMenu';
import DashboardIntentSection from './DashboardIntentSection';
import DashboardBannerCarousel from './DashboardBannerCarousel';
import './CustomerDashboard.css';

const FILTERS = [
  {
    id: 'ALL',
    label: 'All',
    helper: 'Every application',
    predicate: () => true,
  },
  {
    id: 'NEEDS_ACTION',
    label: 'Needs action',
    helper: 'Docs / OTP pending',
    predicate: (lead) => deriveBucket(lead) === 'NEEDS_ACTION',
  },
  {
    id: 'IN_PROGRESS',
    label: 'In progress',
    helper: 'Under review',
    predicate: (lead) => deriveBucket(lead) === 'IN_PROGRESS',
  },
  {
    id: 'APPROVED',
    label: 'Approved / disbursed',
    helper: 'Ready to view status',
    predicate: (lead) => deriveBucket(lead) === 'APPROVED',
  },
  {
    id: 'CLOSED',
    label: 'Closed',
    helper: 'Rejected / closed',
    predicate: (lead) => deriveBucket(lead) === 'CLOSED',
  },
];

function deriveBucket(lead = {}) {
  const status = normalize(lead.leadStatus);
  const stage = normalize(lead.loanStage) || normalize(lead.applicationStage);
  const combined = `${status} ${stage}`.trim();

  if (!combined) return 'NEEDS_ACTION';
  if (combined.includes('reject') || combined.includes('closed') || combined.includes('not eligible')) {
    return 'CLOSED';
  }
  if (combined.includes('disbur') || combined.includes('approved') || combined.includes('sanction')) {
    return 'APPROVED';
  }
  if (combined.includes('new') || combined.includes('follow') || combined.includes('pending') || combined.includes('await')) {
    return 'NEEDS_ACTION';
  }
  return 'IN_PROGRESS';
}

const normalize = (value) => (value ? value.toString().toLowerCase() : '');

const parseAmount = (value) => {
  if (value === null || value === undefined) return 0;
  const numeric = Number(value.toString().replace(/[^0-9.]/g, ''));
  return Number.isNaN(numeric) ? 0 : numeric;
};

const formatCurrency = (value) => {
  const amount = parseAmount(value);
  if (!amount) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (value) => {
  if (!value) return '--';
  const jsDate = new Date(value);
  if (!Number.isNaN(jsDate.getTime())) {
    return jsDate.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const match = value.match(/(\d{2})-(\d{2})-(\d{4})\s?(\d{2}:\d{2}:\d{2})?/);
  if (match) {
    const [, dd, mm, yyyy, time] = match;
    const isoString = `${yyyy}-${mm}-${dd}${time ? `T${time}` : ''}`;
    const parsed = new Date(isoString);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }
  return value;
};

const parseRemarks = (remarks = '') => {
  if (!remarks) return [];

  return remarks
    .split('Previous remarks:')
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk, index) => {
      const bracketMatches = chunk.match(/\[[^\]]+\]/g) || [];
      const timestamp = bracketMatches[0]?.replace(/\[|\]/g, '').trim();
      const source = bracketMatches[1]?.replace(/\[|\]/g, '').trim();

      let note = chunk;
      bracketMatches.forEach((match) => {
        note = note.replace(match, '');
      });
      note = note.replace(/Original status:[^.]+/i, '').trim();

      return {
        id: `${timestamp || 'remark'}-${index}`,
        timestamp: timestamp || 'Latest update',
        source,
        message: note || 'Status updated by lender',
      };
    });
};

const shouldUseBizzie = (lead = {}) => {
  const product = normalize(lead.productType);
  return product.includes('commercial vehicle') || product.includes('business loan');
};

const isApplicationComplete = (lead = {}) => {
  // Determine from chat history or last messages whether application KYC/upload steps are complete.
  const history = lead.chatHistory || lead.chatHistoryArray || lead.chat_history || [];
  if (!Array.isArray(history) || history.length === 0) return false;
  const last = history[history.length - 1];
  const text = (last?.messageText || '').toString();
  if (!text) return false;
  // Common completion indicators
  const completionRegex = /(uploaded successfully|application completed|thank you|disbursed|completed successfully)/i;
  return completionRegex.test(text);
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const customer = useChatStore((state) => state.customer);
  const authToken = useChatStore((state) => state.authToken);
  const logout = useChatStore((state) => state.logout);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('ALL');
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    const loadLeads = async () => {
      const mobile = customer?.mobile || customer?.mobileNumber || customer?.phoneNumber;

      if (!customer || !mobile) {
        console.error('[Dashboard] Customer data missing:', customer);
        setLoading(false);
        setError('Please login to view your applications');

        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      try {
        setLoading(true);
        const result = await fetchLeadsByMobile(mobile, authToken);

        if (result.success) {
          const leadsData = Array.isArray(result.data) ? result.data : [result.data];
          setLeads(leadsData);
          setError(null);
        } else {
          setError(result.message);
          showToast(result.message, 'error');
        }
      } catch (err) {
        console.error('Error loading leads:', err);
        setError('Failed to load your applications');
        showToast('Failed to load your applications', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadLeads();
  }, [customer, navigate]);

  const filterCounts = useMemo(() => {
    return FILTERS.reduce((acc, filter) => {
      acc[filter.id] = filter.id === 'ALL' ? leads.length : leads.filter(filter.predicate).length;
      return acc;
    }, {});
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const filter = FILTERS.find((item) => item.id === selectedFilter) || FILTERS[0];
    return leads.filter(filter.predicate);
  }, [leads, selectedFilter]);

  useEffect(() => {
    if (!filteredLeads.length) {
      setSelectedLead(null);
      return;
    }

    setSelectedLead((prev) => {
      if (prev && filteredLeads.some((lead) => lead.id === prev.id)) {
        return prev;
      }
      return filteredLeads[0];
    });
  }, [filteredLeads]);

  const summaryStats = useMemo(() => {
    if (!leads.length) {
      return {
        total: 0,
        needsAction: 0,
        approved: 0,
        closed: 0,
        totalAmount: 0,
      };
    }

    return leads.reduce(
      (acc, lead) => {
        const bucket = deriveBucket(lead);
        acc.total += 1;
        acc.totalAmount += parseAmount(lead.loanAmount);
        if (bucket === 'NEEDS_ACTION') acc.needsAction += 1;
        if (bucket === 'APPROVED') acc.approved += 1;
        if (bucket === 'CLOSED') acc.closed += 1;
        return acc;
      },
      { total: 0, needsAction: 0, approved: 0, closed: 0, totalAmount: 0 },
    );
  }, [leads]);


  const latestLead = selectedLead || filteredLeads[0] || leads[0] || null;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleViewProfile = () => {
    showToast('Profile view will be available soon.', 'info');
  };

  const handleSupport = () => {
    const supportUrl = 'https://wa.me/9662267474?text=Hi%20GrowBizh%20team%2C%20I%20need%20support%20for%20my%20application';
    window.open(supportUrl, '_blank', 'noopener,noreferrer');
  };

  const selectedLeadRemarks = useMemo(() => parseRemarks(selectedLead?.remarks || ''), [selectedLead]);

  const handleResumeLatest = () => {
    if (latestLead) {
      resumeLead(latestLead);
    } else {
      showToast('No application available to resume yet.', 'info');
    }
  };

  const profileMenu = (
    <DashboardProfileMenu
      onResumeLatest={handleResumeLatest}
      onSupport={handleSupport}
      onViewProfile={handleViewProfile}
      onLogout={handleLogout}
    />
  );

  const handleNewApplication = () => {
    // Reset only chat/session state but keep customer logged in so header remains authenticated.
    const resetChat = useChatStore.getState().resetChat;
    if (typeof resetChat === 'function') {
      resetChat();
    } else {
      // Fallback: clear session fields without clearing customer
      useChatStore.setState({ chatSessionId: null, loanId: null, chatHistory: [] });
    }
    navigate('/');
  };

  if (loading) {
    return (
      <>
        <HeaderWithoutLang rightAddon={profileMenu} />
        <div
          style={{
            minHeight: '70vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontFamily: 'Inter, sans-serif',
            background: '#f8f9fa',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '50px',
                height: '50px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #238EF8',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px',
              }}
            />
            <div style={{ fontSize: '18px', color: '#666' }}>Loading your applications...</div>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <Footer />
      </>
    );
  }

  if (error && !customer) {
    return (
      <>
        <HeaderWithoutLang rightAddon={profileMenu} />
        <div
          style={{
            minHeight: '70vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontFamily: 'Inter, sans-serif',
            background: '#f8f9fa',
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
            <h2
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '24px',
                fontWeight: '600',
                color: '#0C0D0E',
                marginBottom: '10px',
              }}
            >
              Login Required
            </h2>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '16px',
                color: '#666',
                marginBottom: '20px',
              }}
            >
              {error}
            </p>
            <p
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                color: '#999',
              }}
            >
              Redirecting to home page...
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const getStatusClass = (lead) => {
    const bucket = deriveBucket(lead);
    switch (bucket) {
      case 'NEEDS_ACTION':
        return 'status-new';
      case 'APPROVED':
        return 'status-approved';
      case 'CLOSED':
        return 'status-closed';
      default:
        return 'status-in-progress';
    }
  };

  const getStatusLabel = (lead) => lead.leadStatus || lead.loanStage || 'In progress';

  const resumeLead = (lead) => {
    if (!lead) return;

    (async () => {
      try {
        // Determine authoritative chatSessionId: prefer lead.chatSessionId, else try fetch by loanRequestId
        let sessionId = lead.chatSessionId || null;

        if (!sessionId && (lead.loanRequestId || lead.id)) {
          const lrId = lead.loanRequestId || lead.id;
          try {
            const resp = await axios.get(`${CREDIT_API_URL}chat-history/loan/${lrId}`);
            if (resp.data && Array.isArray(resp.data.data) && resp.data.data.length > 0) {
              sessionId = resp.data.data[0].chatSessionId || sessionId;
            }
          } catch (err) {
            console.warn('[Dashboard] failed fetching chat-history by loan id', err);
          }
        }

        // set a short suppression so Bizzie or other auto-redirect checks don't run while we set state and navigate
        useChatStore.getState().setSuppressAutoRedirect(5000);

        // update store with resolved session id and customerId
        useChatStore.setState({
          customer: {
            ...(customer || {}),
            customerId: lead.customerId,  // ✅ Include customerId from lead
            chatSessionId: sessionId || lead.chatSessionId || null,
            loanRequestId: lead.loanRequestId || lead.id,
          },
          chatSessionId: sessionId || lead.chatSessionId || null,
          loanId: lead.loanRequestId || lead.id,
        });

        if (shouldUseBizzie(lead)) {
          navigate('/bizzie');
          return;
        }

    // Note: opening lender portal has been intentionally removed per UX requirement.

        showToast('Our team will reach out with the next steps shortly.', 'info');
      } catch (e) {
        console.error('[Dashboard] resumeLead failed', e);
      }
    })();
  };

  const openLenderPortal = (lead) => {
    if (lead?.lenderInfo?.lenderLink) {
      window.open(lead.lenderInfo.lenderLink, '_blank', 'noopener');
    } else {
      showToast('Lender portal is not available for this lead yet.', 'info');
    }
  };

  return (
    <>
      <HeaderWithoutLang rightAddon={profileMenu} />
      <section className="dashboard-page">
        <div className="section-container dashboard-container">
          <div className="dashboard-header">
            <div>
              <p className="dashboard-eyebrow">GrowBizh Credit</p>
              <h1>My Applications</h1>
              <p>Track every application, stay on top of lender updates and continue where you left off.</p>
            </div>
            <button type="button" className="dashboard-primary-btn" onClick={handleNewApplication}>
              + New application
            </button>
          </div>

          <DashboardIntentSection />

          <div className="dashboard-summary">
            <div className="dashboard-summary-card">
              <p>Approved / disbursed</p>
              <strong>{summaryStats.approved}</strong>
              <span>Ready to track status</span>
            </div>
            <div className="dashboard-summary-card">
              <p>Total requested amount</p>
              <strong>{formatCurrency(summaryStats.totalAmount)}</strong>
              <span>Across all applications</span>
            </div>
          </div>

          <div className="dashboard-filters">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`dashboard-filter-chip ${selectedFilter === filter.id ? 'active' : ''}`}
                onClick={() => setSelectedFilter(filter.id)}
              >
                <div>
                  <span>{filter.label}</span>
                  <p>{filter.helper}</p>
                </div>
                <span className="chip-count">{filterCounts[filter.id] ?? 0}</span>
              </button>
            ))}
          </div>

          {error && customer && (
            <div className="dashboard-inline-alert">
              <strong>Heads up:</strong> {typeof error === 'string' ? error : JSON.stringify(error)}
            </div>
          )}

          <div className="dashboard-content">
            <div className="dashboard-list">
              {!filteredLeads.length ? (
                <div className="dashboard-empty-state">
                  <img src={`${ASSETS_URL}images/chatbot-icon.png`} alt="No applications" />
                  <h3>No applications found</h3>
                  <p>Start a new request to see it listed here.</p>
                  <button type="button" className="dashboard-primary-btn" onClick={handleNewApplication}>
                    Start application
                  </button>
                </div>
              ) : (
                filteredLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className={`dashboard-card ${selectedLead?.id === lead.id ? 'is-selected' : ''}`}
                    onClick={() => setSelectedLead(lead)}
                  >
                    <div className="dashboard-card-header">
                      <div>
                        <p className="dashboard-loan-type">{lead.productType || 'Loan application'}</p>
                        <p className="dashboard-loan-id">ID: {lead.id}</p>
                      </div>
                      <span className={`dashboard-status-pill ${getStatusClass(lead)}`}>{getStatusLabel(lead)}</span>
                    </div>

                    <div className="dashboard-meta-grid">
                      <div className="dashboard-meta-item">
                        <p>Loan amount</p>
                        <strong>{formatCurrency(lead.loanAmount)}</strong>
                      </div>
                      <div className="dashboard-meta-item">
                        <p>Lender</p>
                        <strong>{lead.lenderInfo?.lenderId || 'Assigning'}</strong>
                      </div>
                      <div className="dashboard-meta-item">
                        <p>Stage</p>
                        <strong>{lead.loanStage || lead.applicationStage || 'In review'}</strong>
                      </div>
                      <div className="dashboard-meta-item">
                        <p>Last updated</p>
                        <strong>{formatDate(lead.lastUpdated)}</strong>
                      </div>
                    </div>

                    <div className="dashboard-timeline">
                      <span>Applied on {formatDate(lead.createdAt)}</span>
                      <span>
                        Assigned RM: {lead.isAssignedToCurrentUser ? 'You' : lead.assignedTo ? 'GrowBizh RM' : 'Pending assignment'}
                      </span>
                    </div>

                    <div className="dashboard-actions">
                      {(() => {
                        const approved = deriveBucket(lead) === 'APPROVED';
                        const bizzieType = shouldUseBizzie(lead);
                        const completed = isApplicationComplete(lead);
                        // "View status" button hidden per requirement
                        if (approved) {
                          return null;
                        }

                        if (bizzieType && !completed) {
                          return (
                            <button
                              type="button"
                              className="dashboard-primary-btn small"
                              onClick={(event) => {
                                event.stopPropagation();
                                resumeLead(lead);
                              }}
                            >
                              Continue application
                            </button>
                          );
                        }

                        // Otherwise, do not show primary action (or show a neutral status)
                        return null;
                      })()}

                      {/* Open lender portal intentionally hidden per UX requirement */}
                    </div>
                  </div>
                ))
              )}
            </div>

            <aside className="dashboard-detail-panel">
              {selectedLead ? (
                <>
                  <span className="detail-pill">Loan ID: {selectedLead.id}</span>
                  <h3>{selectedLead.productType || 'Loan application'}</h3>
                  <p className="detail-stage">
                    Current status — <strong>{selectedLead.loanStage || selectedLead.leadStatus || 'In review'}</strong>
                  </p>

                  <div className="detail-grid">
                    <div>
                      <p>Requested amount</p>
                      <strong>{formatCurrency(selectedLead.loanAmount)}</strong>
                    </div>
                    <div>
                      <p>Lender</p>
                      <strong>{selectedLead.lenderInfo?.lenderId || 'Assigning'}</strong>
                    </div>
                    <div>
                      <p>Customer ID</p>
                      <strong>{selectedLead.customerId || '—'}</strong>
                    </div>
                    <div>
                      <p>Partner customer ID</p>
                      <strong>{selectedLead.partnerCustomerId || selectedLead.lenderInfo?.partnerCustomerId || '—'}</strong>
                    </div>
                  </div>

                  <div className="detail-meta-row">
                    <div>
                      <p>Created on</p>
                      <strong>{formatDate(selectedLead.createdAt)}</strong>
                    </div>
                    <div>
                      <p>Last updated</p>
                      <strong>{formatDate(selectedLead.lastUpdated)}</strong>
                    </div>
                  </div>

                  {selectedLeadRemarks.length ? (
                    <div className="remarks-section">
                      <div className="remarks-header">
                        <h4>Latest lender updates</h4>
                        <span>{selectedLeadRemarks.length} updates</span>
                      </div>
                      <div className="remarks-list">
                        {selectedLeadRemarks.map((remark) => (
                          <div className="remarks-item" key={remark.id}>
                            <div className="remarks-meta">
                              <span>{remark.timestamp}</span>
                              {remark.source && <span className="remarks-source">{remark.source}</span>}
                            </div>
                            <p>{remark.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="detail-placeholder">
                      <h4>No lender updates yet</h4>
                      <p>We will notify you as soon as there is a movement on this application.</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="detail-placeholder">
                  <h4>Select an application</h4>
                  <p>Pick any application from the left to view its journey, lender updates and action items.</p>
                </div>
              )}
            </aside>
          </div>

          <DashboardBannerCarousel />
        </div>
      </section>
      <Footer />
    </>
  );
};

export default CustomerDashboard;
